import { useState, useEffect } from 'react';
import { createLogger } from '@/lib/logger';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import SkeletonRow from '@/components/SkeletonRow';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Trophy, Check, Lock } from 'lucide-react-native';
import { LinearGradient } from 'expo-linear-gradient';

const log = createLogger('PROGRESSION');

interface ProgressionTier {
  id: number;
  name: string;
  color: string;
  requirements: {
    workoutsLogged: number;
    activeWeeks: number;
    streakBest?: number;
    verifiedLifts?: number;
    duelsCompleted?: number;
    accountAgeDays?: number;
  };
  current: {
    workoutsLogged: number;
    activeWeeks: number;
    streakBest: number;
    verifiedLifts: number;
    duelsCompleted: number;
    accountAgeDays: number;
  };
}

export default function ProgressionScreen() {
  const { profile } = useAuth();
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [currentTier, setCurrentTier] = useState(0);
  const [progress, setProgress] = useState<ProgressionTier[]>([]);

  const tiers: Omit<ProgressionTier, 'current'>[] = [
    {
      id: 1,
      name: 'Fresh Plates',
      color: '#CD7F32',
      requirements: { workoutsLogged: 1, activeWeeks: 0 },
    },
    {
      id: 2,
      name: 'Iron Habit',
      color: '#FF6B35',
      requirements: { workoutsLogged: 25, activeWeeks: 6 },
    },
    {
      id: 3,
      name: 'Built Different',
      color: '#FFD700',
      requirements: { workoutsLogged: 75, activeWeeks: 16, streakBest: 7 },
    },
    {
      id: 4,
      name: 'Barbell Authority',
      color: '#E5E4E2',
      requirements: { workoutsLogged: 150, activeWeeks: 32, verifiedLifts: 5 },
    },
    {
      id: 5,
      name: 'Forged by Iron',
      color: '#FF4444',
      requirements: { workoutsLogged: 300, activeWeeks: 52, duelsCompleted: 10, accountAgeDays: 365 },
    },
    {
      id: 6,
      name: 'DUEL Standard',
      color: '#B9F2FF',
      requirements: {
        workoutsLogged: 500,
        activeWeeks: 78,
        verifiedLifts: 25,
        duelsCompleted: 30,
        accountAgeDays: 730,
      },
    },
  ];

  useEffect(() => {
    loadProgress();
  }, []);

  const loadProgress = async () => {
    if (!profile) return;

    try {
      const { count: workoutCount } = await supabase
        .from('workouts')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', profile.id);

      // Count distinct ISO weeks with at least one workout
      const { data: workoutDates } = await supabase
        .from('workouts')
        .select('performed_at')
        .eq('user_id', profile.id);
      const activeWeeksCount = workoutDates
        ? new Set(workoutDates.map((w: any) => {
            const d = new Date(w.performed_at);
            const jan1 = new Date(d.getFullYear(), 0, 1);
            return `${d.getFullYear()}-${Math.ceil(((d.getTime() - jan1.getTime()) / 86400000 + jan1.getDay() + 1) / 7)}`;
          })).size
        : 0;

      const { data: streakData } = await supabase
        .from('user_streaks')
        .select('longest_streak')
        .eq('user_id', profile.id)
        .maybeSingle();

      const { count: verifiedCount } = await supabase
        .from('pr_entries')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', profile.id)
        .eq('is_verified', true);

      const { count: duelCount } = await supabase
        .from('duels')
        .select('*', { count: 'exact', head: true })
        .or(`challenger_id.eq.${profile.id},opponent_id.eq.${profile.id}`)
        .eq('status', 'completed');

      const accountAgeDays = Math.floor(
        (Date.now() - new Date(profile.created_at).getTime()) / (1000 * 60 * 60 * 24)
      );

      const current = {
        workoutsLogged: workoutCount || 0,
        activeWeeks: activeWeeksCount,
        streakBest: streakData?.longest_streak || 0,
        verifiedLifts: verifiedCount || 0,
        duelsCompleted: duelCount || 0,
        accountAgeDays,
      };

      const tiersWithProgress = tiers.map(tier => ({
        ...tier,
        current,
      }));

      setProgress(tiersWithProgress);

      let userTier = 0;
      for (let i = tiers.length - 1; i >= 0; i--) {
        const tier = tiers[i];
        const meetsAll = Object.entries(tier.requirements).every(([key, value]) => {
          const currentKey = key as keyof typeof current;
          return current[currentKey] >= value;
        });
        if (meetsAll) {
          userTier = i;
          break;
        }
      }
      setCurrentTier(userTier);
    } catch (error) {
      log.error('Error loading progression:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateTierProgress = (tier: ProgressionTier): number => {
    const requirements = Object.entries(tier.requirements);
    if (requirements.length === 0) return 100;

    const progresses = requirements.map(([key, required]) => {
      const currentKey = key as keyof typeof tier.current;
      const current = tier.current[currentKey];
      return Math.min((current / required) * 100, 100);
    });

    return Math.min(...progresses);
  };

  if (loading) {
    return (
      <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <View style={styles.centerContainer}>
          <SkeletonRow rows={4} />
        </View>
      </View>
    );
  }

  return (
    <SafeAreaView style={{ backgroundColor: theme.colors.background, flex: 1 }} edges={['top']}>
    <View style={[styles.container]}>
      <LinearGradient
        colors={[theme.colors.background, theme.colors.surfaceVariant]}
        style={[styles.header, { borderBottomColor: theme.colors.border }]}>
        <View style={styles.headerTop}>
          <View style={{ width: 28 }} />
          <Text style={[styles.headerTitle, { color: theme.colors.accent }]}>
            ROAD TO DUEL STANDARD
          </Text>
          <View style={{ width: 28 }} />
        </View>

        <View style={[styles.currentTierCard, { backgroundColor: progress[currentTier]?.color }]}>
          <Trophy size={32} color={theme.colors.text} />
          <View style={styles.currentTierInfo}>
            <Text style={[styles.currentTierLabel, { color: theme.colors.text }]}>Current Tier</Text>
            <Text style={[styles.currentTierName, { color: theme.colors.text }]}>{progress[currentTier]?.name || 'Loading...'}</Text>
          </View>
        </View>
      </LinearGradient>

      <ScrollView contentContainerStyle={styles.content}>
        {progress.map((tier, index) => {
          const isUnlocked = index <= currentTier;
          const isCurrent = index === currentTier;
          const isNext = index === currentTier + 1;
          const progress = calculateTierProgress(tier);

          return (
            <View
              key={tier.id}
              style={[
                styles.tierCard,
                {
                  backgroundColor: theme.colors.surface,
                  borderColor: isUnlocked ? tier.color : theme.colors.border,
                  borderWidth: isUnlocked ? 2 : 1,
                  opacity: isUnlocked || isNext ? 1 : 0.5,
                },
              ]}>
              <View style={styles.tierHeader}>
                <View
                  style={[
                    styles.tierIcon,
                    {
                      backgroundColor: isUnlocked ? tier.color + '20' : theme.colors.surfaceVariant,
                      borderColor: tier.color,
                    },
                  ]}>
                  {isUnlocked ? (
                    <Check size={28} color={tier.color} strokeWidth={3} />
                  ) : (
                    <Lock size={28} color={theme.colors.textTertiary} />
                  )}
                </View>
                <View style={styles.tierInfo}>
                  <Text style={[styles.tierName, { color: isUnlocked ? tier.color : theme.colors.text }]}>
                    {tier.name}
                  </Text>
                  {isCurrent && (
                    <View style={[styles.currentBadge, { backgroundColor: tier.color + '20' }]}>
                      <Text style={[styles.currentBadgeText, { color: tier.color }]}>CURRENT</Text>
                    </View>
                  )}
                  {isNext && (
                    <View style={[styles.nextBadge, { backgroundColor: tier.color + '20' }]}>
                      <Text style={[styles.nextBadgeText, { color: tier.color }]}>NEXT</Text>
                    </View>
                  )}
                </View>
              </View>

              {(isNext || !isUnlocked) && (
                <>
                  <View style={styles.progressBarContainer}>
                    <View
                      style={[
                        styles.progressBar,
                        { backgroundColor: theme.colors.surfaceVariant },
                      ]}>
                      <View
                        style={[
                          styles.progressFill,
                          { width: `${progress}%`, backgroundColor: tier.color },
                        ]}
                      />
                    </View>
                    <Text style={[styles.progressText, { color: theme.colors.textSecondary }]}>
                      {Math.floor(progress)}%
                    </Text>
                  </View>

                  <View style={styles.requirements}>
                    <Text style={[styles.requirementsTitle, { color: theme.colors.textSecondary }]}>
                      Requirements:
                    </Text>
                    {Object.entries(tier.requirements).map(([key, required]) => {
                      const currentKey = key as keyof typeof tier.current;
                      const current = tier.current[currentKey];
                      const labels: Record<string, string> = {
                        workoutsLogged: 'Workouts Logged',
                        activeWeeks: 'Active Weeks',
                        streakBest: 'Best Streak',
                        verifiedLifts: 'Verified Lifts',
                        duelsCompleted: 'Duels Completed',
                        accountAgeDays: 'Account Age (days)',
                      };

                      return (
                        <View key={key} style={styles.requirementRow}>
                          <Text style={[styles.requirementLabel, { color: theme.colors.text }]}>
                            {labels[key]}
                          </Text>
                          <Text
                            style={[
                              styles.requirementValue,
                              {
                                color: current >= required ? tier.color : theme.colors.textSecondary,
                              },
                            ]}>
                            {current} / {required}
                          </Text>
                        </View>
                      );
                    })}
                  </View>
                </>
              )}
            </View>
          );
        })}
      </ScrollView>
    </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  header: {
    paddingTop: 12,
    paddingBottom: 20,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
  },
  headerTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  backButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '900',
    letterSpacing: 1,
  },
  currentTierCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    padding: 20,
    borderRadius: 2,
  },
  currentTierInfo: {
    flex: 1,
  },
  currentTierLabel: {
    fontSize: 14,
    opacity: 0.9,
  },
  currentTierName: {
    fontSize: 24,
    fontWeight: '900',
  },
  content: {
    padding: 16,
    paddingBottom: 40,
  },
  tierCard: {
    borderRadius: 2,
    padding: 16,
    marginBottom: 16,
  },
  tierHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    marginBottom: 16,
  },
  tierIcon: {
    width: 56,
    height: 56,
    borderRadius: 4,
    borderWidth: 2,
    justifyContent: 'center',
    alignItems: 'center',
  },
  tierInfo: {
    flex: 1,
  },
  tierName: {
    fontSize: 20,
    fontWeight: '900',
    marginBottom: 4,
  },
  currentBadge: {
    alignSelf: 'flex-start',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 2,
  },
  currentBadgeText: {
    fontSize: 11,
    fontWeight: '700',
  },
  nextBadge: {
    alignSelf: 'flex-start',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 2,
  },
  nextBadgeText: {
    fontSize: 11,
    fontWeight: '700',
  },
  progressBarContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
  },
  progressBar: {
    flex: 1,
    height: 8,
    borderRadius: 4,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
  },
  progressText: {
    fontSize: 14,
    fontWeight: '700',
    minWidth: 45,
  },
  requirements: {
    gap: 8,
  },
  requirementsTitle: {
    fontSize: 14,
    fontWeight: '700',
    marginBottom: 4,
  },
  requirementRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 4,
  },
  requirementLabel: {
    fontSize: 14,
  },
  requirementValue: {
    fontSize: 14,
    fontWeight: '700',
  },
});
